import React, { memo, useEffect, useRef, useState } from 'react'

import { CSSTransition } from 'react-transition-group'
import { motion, useAnimation } from 'framer-motion'

import styles from './styles.module.scss'
import '../filesGroupAnimation.scss'
import cn from 'classnames'
import useMediaQuery from '@mui/material/useMediaQuery'

import { ReactComponent as TrashIcon } from '../../../assets/icons/trashIcon.svg'
import { ReactComponent as EditIcon } from '../../../assets/icons/edit.svg'
import { ReactComponent as CloseIcon } from '../../../assets/icons/close-white.svg'
import { ReactComponent as CalcOfFilesIcon } from '../../../assets/icons/calcOfFiles.svg'
import { ReactComponent as DeleteFileIcon } from '../../../assets/icons/close.svg'
import { ReactComponent as HistoryIcon } from '../../../assets/icons/revers.svg'

import VesselNameHistory from './VesselNameHistory/VesselNameHistory'
import useVesselNameHistory from './VesselNameHistory/useVesselNameHistory'
import ImageComponent from '../../../UI/Image/ImageComponent'
import PhotoGalleryModal from '../../../UI/PhotoGalleryModal/PhotoGalleryModal'

import { isDefaultVesselImg } from '../../../helpers/helpers'
import { SmartTooltip } from '../../../UI/Tooltip/Tooltip'

const ExperienceGroupItem = memo(({ type, data, onDelete, onEdit, deleteFile, isActive, isSwiped, onSwipeChange }) => {
  const [visibleListOfFiles, setVisibleListOfFiles] = useState(false)
  const [expTypeSea, setExpTypeSea] = useState(true)
  const [uncontrolledIsSwiped, setUncontrolledIsSwiped] = useState(false)
  const [isGalleryOpen, setIsGalleryOpen] = useState(false)
  const [activeIndex, setActiveIndex] = useState(0)
  const isTabletOrMobile = useMediaQuery('(max-width: 1024px)')
  const isSwipedValue = typeof isSwiped === 'boolean' ? isSwiped : uncontrolledIsSwiped

  const vesselPhotoUrl = data?.vessel?.mt_photo_mini_url || ''
  const hasVesselPhoto = Boolean(vesselPhotoUrl) && !isDefaultVesselImg(vesselPhotoUrl)

  const swipeRef = useRef({
    startX: 0,
    startY: 0,
    pointerId: null,
    isTracking: false,
    isHorizontal: false,
  })

  const nodeRef = useRef(null)
  const control = useAnimation()

  useEffect(() => {
    if (visibleListOfFiles) {
      control.start((i) => ({
        opacity: 1,
        x: 0,
        transition: { delay: i * 0.1, type: 'spring' },
      }))
    } else {
      control.start((i) => ({
        opacity: 0,
        x: 0,
        transition: { delay: i * 0.1, type: 'spring' },
      }))
    }

    // eslint-disable-next-line
  }, [visibleListOfFiles])

  useEffect(() => {
    if (data.experience_type !== 'sea') {
      setExpTypeSea(false)
    } else {
      setExpTypeSea(true)
    }
  }, [data])

  const { props, closeHistory, getRenameHistory, loading } = useVesselNameHistory(data.vessel.id)

  useEffect(() => {
    if (!isActive) closeHistory()
  }, [isActive, closeHistory])

  const handleFilesEnter = () => {
    setVisibleListOfFiles(true)
  }
  const handleFilesLeave = () => {
    setVisibleListOfFiles(false)
  }

  const isInteractiveTarget = (target) => {
    if (!target?.closest) return false
    return Boolean(target.closest('button, a, input, label, textarea, select, svg'))
  }

  const handlePointerDown = (e) => {
    if (isInteractiveTarget(e.target)) return

    swipeRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      pointerId: e.pointerId,
      isTracking: true,
      isHorizontal: false,
    }

    try {
      e.currentTarget.setPointerCapture(e.pointerId)
    } catch (_) {
      // ignore
    }
  }

  const handlePointerMove = (e) => {
    const st = swipeRef.current
    if (!st.isTracking || st.pointerId !== e.pointerId) return

    const dx = e.clientX - st.startX
    const dy = e.clientY - st.startY

    if (!st.isHorizontal) {
      if (Math.abs(dx) < 8 && Math.abs(dy) < 8) return
      st.isHorizontal = Math.abs(dx) > Math.abs(dy)
    }

    if (st.isHorizontal) {
      e.preventDefault()
    }
  }

  const handlePointerEnd = (e) => {
    const st = swipeRef.current
    if (!st.isTracking || st.pointerId !== e.pointerId) return

    const dx = e.clientX - st.startX
    const dy = e.clientY - st.startY

    if (Math.abs(dx) > Math.abs(dy)) {
      if (dx < -40) {
        if (typeof onSwipeChange === 'function') onSwipeChange(true)
        else setUncontrolledIsSwiped(true)
      }
      if (dx > 30) {
        if (typeof onSwipeChange === 'function') onSwipeChange(false)
        else setUncontrolledIsSwiped(false)
      }
    }

    swipeRef.current.isTracking = false
    swipeRef.current.pointerId = null
  }

  const openGallery = (index = 0) => {
    setActiveIndex(index)
    setIsGalleryOpen(true)
  }

  const closeGallery = () => {
    setIsGalleryOpen(false)
  }

  return (
    <div
      className={cn(styles.card, { [styles.card__active]: isActive, [styles.swiped]: isSwipedValue })}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerEnd}
      onPointerCancel={handlePointerEnd}
    >
      <div className={styles.cardInfo}>
        <div className={styles.rank}>{data.rank}</div>

        <div className={styles.cardInfoBody}>
          <div className={styles.cardMainInfo}>
            {expTypeSea && isTabletOrMobile && hasVesselPhoto && (
              <div className={styles.vesselPreview}>
                <ImageComponent src={vesselPhotoUrl} alt='vassel' className={styles.vesselPreviewImg} />
              </div>
            )}
            <div className={styles.field}>
              <div className={styles.fieldName}>Location:</div>

              <div className={styles.fieldValue}>{expTypeSea ? 'Onboard Job' : 'Shore-Based Job'}</div>
            </div>

            {expTypeSea ? (
              <div className={styles.field}>
                <div className={styles.fieldName}>Vessel name:</div>

                <div className={styles.fieldValue}>
                  {!isTabletOrMobile && hasVesselPhoto ? (
                    <SmartTooltip
                      placement='bottom'
                      title={<ImageComponent src={vesselPhotoUrl} alt='vassel' className={styles.vesselTooltipImg} />}
                    >
                      <b className={styles.vesselName}>{data.vessel.name}</b>
                    </SmartTooltip>
                  ) : (
                    <b className={cn(styles.vesselName, { [styles.vesselNameDisabled]: isTabletOrMobile })}>
                      {data.vessel.name}
                    </b>
                  )}

                  <span className={styles.historyIcon}>
                    <SmartTooltip
                      placement='bottom'
                      title={
                        <VesselNameHistory
                          id={data.vessel.id}
                          getRenameHistory={getRenameHistory}
                          loading={loading}
                          {...props}
                        />
                      }
                    >
                      <HistoryIcon />
                    </SmartTooltip>
                  </span>
                </div>
              </div>
            ) : (
              <div className={styles.field}>
                <div className={styles.fieldName}>Country:</div>

                <div className={styles.fieldValue}>{data?.country?.name || '–'}</div>
              </div>
            )}

            {expTypeSea ? (
              <div className={styles.field}>
                <div className={styles.fieldName}>Vessel type:</div>

                <div className={styles.fieldValue}>{data.vesselType}</div>
              </div>
            ) : (
              <div className={styles.field}>
                <div className={styles.fieldName}>City:</div>

                <div className={styles.fieldValue}>{data?.city?.name || '–'}</div>
              </div>
            )}

            <div className={styles.field}>
              <div className={styles.fieldName}>Period:</div>

              <div className={styles.fieldValue}>{data.period}</div>
            </div>

            {expTypeSea && (
              <div className={styles.field}>
                <div className={styles.fieldName}>Engine:</div>

                <div className={styles.fieldValue}>{data.engineType}</div>
              </div>
            )}
          </div>

          <div className={styles.cardAdditionalInfo}>
            <div className={styles.files}>
              {data?.files?.length ? (
                <div
                  key={data.files[0].id}
                  className={styles.file}
                  onMouseEnter={!isTabletOrMobile ? handleFilesEnter : undefined}
                >
                  <div key={data.files[0].id} className={styles.file}>
                    <div className={styles.calcOfFiles}>
                      <label className={styles.calcOfFilesLabel}>{data.files.length}</label>

                      <CalcOfFilesIcon />
                    </div>

                    <a
                      rel='noopener noreferrer'
                      target='_blank'
                      href={data.files[0].file}
                      onClick={(e) => {
                        if (!isTabletOrMobile) return
                        e.preventDefault()
                        openGallery(0)
                      }}
                    >
                      <img src={data.files[0].file_mini} alt='file' />
                    </a>
                  </div>
                </div>
              ) : (
                ''
              )}
            </div>
          </div>

          {!isTabletOrMobile && (
            <CSSTransition
              nodeRef={nodeRef}
              in={visibleListOfFiles}
              timeout={500}
              unmountOnExit
              classNames='listOfFiles'
            >
              <div ref={nodeRef} className='listOfFiles' onMouseLeave={handleFilesLeave}>
                <div className={styles.field}>
                  <div className={styles.fieldName}>Vessel name:</div>

                  <div className={styles.fieldValue}>{data.header}</div>
                </div>

                <div className='files_list'>
                  {data?.files?.length &&
                    data?.files?.map((file) => (
                      <motion.div
                        key={file.id}
                        className={styles.file}
                        custom={file.index}
                        initial={{ opacity: 1, x: 20 }}
                        animate={control}
                        style={{ width: '68px', height: '68px' }}
                      >
                        <div className={styles.deleteFile} onClick={() => deleteFile(file.id, data.id, type, isActive)}>
                          <DeleteFileIcon />
                        </div>

                        <a rel='noopener noreferrer' target='_blank' href={file.file}>
                          <img src={file.file_mini} alt='file' />
                        </a>
                      </motion.div>
                    ))}
                </div>
              </div>
            </CSSTransition>
          )}
        </div>
      </div>

      {data.id !== 'default' && (
        <div className={styles.cardControls}>
          <button className={cn(styles.cardControl, styles.cardControlEdit)} onClick={() => onEdit(data.id, !isActive)}>
            {isActive ? <CloseIcon /> : <EditIcon />}
          </button>

          <button
            className={cn(styles.cardControl, styles.cardControlDelete)}
            onClick={() => onDelete(data.id, data.experience_type, isActive)}
          >
            <TrashIcon />
          </button>
        </div>
      )}

      <PhotoGalleryModal
        open={Boolean(isGalleryOpen && isTabletOrMobile)}
        title={data.rank}
        files={data?.files || []}
        initialIndex={activeIndex}
        onClose={closeGallery}
        onRequestDelete={(file) => deleteFile(file.id, data.id, type, isActive)}
      />
    </div>
  )
})

export default ExperienceGroupItem
